import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../../hooks/useScollToTop";
import TeamLigueModal from "./TeamLigueModal";
import CommissionModal from "./CommissionModal";
import { useNotification } from "../../../components/Notification/NotificationManager";

import {
  getAllTeamLigue,
  deleteTeamMember,
} from "../../../services/teamLigueServices";

import {
  getAllCommissions,
  deleteCommission,
} from "../../../services/commissionsServices";

import {
  FaSearch,
  FaPlus,
  FaUsers,
  FaEnvelope,
  FaImage,
  FaPen,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaSitemap,
  FaUserTie,
  FaLayerGroup,
  FaFilter,
} from "react-icons/fa";

/* ================= Helpers ================= */
const PAGE_SIZE = 20;

const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

/* ================= UI atoms ================= */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({ totalMembers, totalCommissions, assignedMembers }) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Équipe Ligue & Commissions
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Gestion centralisée des commissions de la Ligue et des membres de
              l’équipe, avec rattachement des personnes à leur commission.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[620px]">
            <CompactStat
              icon={<FaUserTie />}
              label="Membres"
              value={totalMembers}
              accent="indigo"
            />
            <CompactStat
              icon={<FaSitemap />}
              label="Commissions"
              value={totalCommissions}
              accent="emerald"
            />
            <CompactStat
              icon={<FaLayerGroup />}
              label="Membres rattachés"
              value={assignedMembers}
              accent="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FiltersBar({
  searchTerm,
  setSearchTerm,
  selectedCommissionId,
  setSelectedCommissionId,
  commissions,
  onReset,
  onCreateCommission,
  onCreateMember,
  loading,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),260px,auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un membre par nom, poste ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <div className="relative">
          <FaFilter className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <select
            value={selectedCommissionId}
            onChange={(e) => setSelectedCommissionId(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          >
            <option value="">Toutes les commissions</option>
            <option value="none">Sans commission</option>
            {commissions.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.nom_commission}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onCreateCommission}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-indigo-300 bg-indigo-50 px-4 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
          >
            <FaPlus />
            Ajouter une commission
          </button>

          <button
            type="button"
            onClick={onCreateMember}
            className="inline-flex h-11 items-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            <FaPlus />
            Ajouter un membre
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberAvatar({ src, alt }) {
  const [imgSrc, setImgSrc] = useState(src || "/img/user_avatar.jpg");

  useEffect(() => {
    setImgSrc(src || "/img/user_avatar.jpg");
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc("/img/user_avatar.jpg")}
      className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

function CommissionAvatar({ src, alt }) {
  const [imgSrc, setImgSrc] = useState(src || "/img/no-image.jpg");

  useEffect(() => {
    setImgSrc(src || "/img/no-image.jpg");
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc("/img/no-image.jpg")}
      className="h-14 w-14 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

function SectionCard({ title, subtitle, children, right }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-end sm:justify-between dark:border-slate-800">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          ) : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function CommissionCard({
  commission,
  onEdit,
  onDelete,
  onFilterMembers,
  isActive,
}) {
  const membersCount = Array.isArray(commission?.members)
    ? commission.members.length
    : 0;
  const articlesCount = Array.isArray(commission?.articles)
    ? commission.articles.length
    : 0;

  return (
    <div
      className={`rounded-3xl border p-4 shadow-sm transition ${
        isActive
          ? "border-indigo-300 bg-indigo-50/70 dark:border-indigo-500/30 dark:bg-indigo-500/10"
          : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
      }`}
    >
      <div className="flex items-start gap-3">
        <CommissionAvatar
          src={commission.photo_url}
          alt={commission.nom_commission}
        />

        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold text-slate-900 dark:text-white">
            {commission.nom_commission}
          </div>

          <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {commission.description_commission || "Aucune description"}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {membersCount} membre{membersCount > 1 ? "s" : ""}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {articlesCount} article{articlesCount > 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => onFilterMembers(commission.id)}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Voir les membres
        </button>

        <button
          onClick={() => onEdit(commission.id)}
          title="Éditer"
          className="inline-flex h-10 items-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-medium text-white transition hover:bg-indigo-700"
        >
          <FaPen />
          {/* Éditer */}
        </button>

        <button
          onClick={() => onDelete(commission)}
          title="Supprimer"
          className="inline-flex h-10 items-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-medium text-white transition hover:bg-red-700"
        >
          <FaTrash />
          {/* Supprimer */}
        </button>
      </div>
    </div>
  );
}

function TeamTableDesktop({
  rows,
  loading,
  commissionNameById,
  onEdit,
  onDelete,
}) {
  return (
    <div className="hidden lg:block">
      <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full min-w-[1100px] table-fixed text-sm">
          <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
            <tr className="text-slate-600 dark:text-slate-300">
              <th className="w-[10%] px-4 py-3 font-semibold">Photo</th>
              <th className="w-[22%] px-4 py-3 font-semibold">Nom</th>
              <th className="w-[16%] px-4 py-3 font-semibold">Poste</th>
              <th className="w-[22%] px-4 py-3 font-semibold">Commission</th>
              <th className="w-[20%] px-4 py-3 font-semibold">Email</th>
              <th className="w-[10%] px-4 py-3 font-semibold text-center">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  Chargement des membres…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  Aucun membre ne correspond aux filtres.
                </td>
              </tr>
            ) : (
              rows.map((m) => (
                <tr
                  key={m.id}
                  className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-4 py-3.5">
                    <MemberAvatar
                      src={m.photo_url}
                      alt={`${m.prenom || ""} ${m.nom || ""}`}
                    />
                  </td>

                  <td className="px-4 py-3.5">
                    <div className="font-semibold text-slate-900 dark:text-white">
                      {m.prenom} {m.nom}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Ordre : {Number(m.ordre || 0)}
                    </div>
                  </td>

                  <td className="px-4 py-3.5 text-slate-700 dark:text-slate-200">
                    {m.poste || "—"}
                  </td>

                  <td className="px-4 py-3.5">
                    <span className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
                      {m.commission_id
                        ? commissionNameById[String(m.commission_id)] || `Commission #${m.commission_id}`
                        : "Sans commission"}
                    </span>
                  </td>

                  <td className="px-4 py-3.5">
                    {m.email ? (
                      <a
                        href={`mailto:${m.email}`}
                        className="break-all text-indigo-600 hover:underline dark:text-indigo-300"
                      >
                        {m.email}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3.5">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => onEdit(m.id)}
                        title="Éditer"
                        className="inline-flex h-9 items-center gap-2 rounded-xl bg-indigo-600 px-3 text-sm font-medium text-white transition hover:bg-indigo-700"
                      >
                        <FaPen />
                        {/* Éditer */}
                      </button>

                      <button
                        onClick={() => onDelete(m.id)}
                        title="Supprimer"
                        className="inline-flex h-9 items-center gap-2 rounded-xl bg-red-600 px-3 text-sm font-medium text-white transition hover:bg-red-700"
                      >
                        <FaTrash />
                        {/* Supprimer */}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeamCardsMobile({ rows, loading, commissionNameById, onEdit, onDelete }) {
  return (
    <div className="lg:hidden">
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement des membres…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Aucun membre ne correspond aux filtres.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((m) => (
            <div
              key={m.id}
              className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <MemberAvatar
                  src={m.photo_url}
                  alt={`${m.prenom || ""} ${m.nom || ""}`}
                />

                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                    {m.prenom} {m.nom}
                  </div>

                  <div className="mt-1 text-[13px] text-slate-600 dark:text-slate-300">
                    {m.poste || "Poste non renseigné"}
                  </div>

                  <div className="mt-2">
                    <span className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
                      {m.commission_id
                        ? commissionNameById[String(m.commission_id)] || `Commission #${m.commission_id}`
                        : "Sans commission"}
                    </span>
                  </div>

                  {m.email ? (
                    <a
                      href={`mailto:${m.email}`}
                      className="mt-2 block break-all text-[12px] text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      {m.email}
                    </a>
                  ) : (
                    <div className="mt-2 text-[12px] text-slate-400">
                      Email non renseigné
                    </div>
                  )}

                  <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                    Ordre d’affichage : {Number(m.ordre || 0)}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={() => onEdit(m.id)}
                  title="Editer"
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  <FaPen />
                  {/* Éditer */}
                </button>

                <button
                  onClick={() => onDelete(m.id)}
                  title="Supprimer"
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700"
                >
                  <FaTrash />
                  {/* Supprimer */}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaginationBar({ page, pageCount, setPage, loading }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page <span className="font-semibold text-slate-900 dark:text-white">{page}</span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page >= pageCount || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* ================= Page ================= */
const TeamLigueAdmin = () => {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [membres, setMembres] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 300);
  const [selectedCommissionId, setSelectedCommissionId] = useState("");

  const [page, setPage] = useState(1);

  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [isCommissionEditMode, setIsCommissionEditMode] = useState(false);
  const [selectedCommissionEditId, setSelectedCommissionEditId] = useState(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [membersData, commissionsData] = await Promise.all([
        getAllTeamLigue(),
        getAllCommissions(),
      ]);

      setMembres(Array.isArray(membersData) ? membersData : []);
      setCommissions(Array.isArray(commissionsData) ? commissionsData : []);
    } catch (error) {
      console.error("Erreur lors du chargement des données :", error);
      addNotification("Erreur lors du chargement des données.", "error");
      setMembres([]);
      setCommissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const sortedCommissions = useMemo(() => {
    return [...commissions].sort((a, b) =>
      normalize(a?.nom_commission).localeCompare(normalize(b?.nom_commission))
    );
  }, [commissions]);

  const commissionNameById = useMemo(() => {
    return sortedCommissions.reduce((acc, c) => {
      acc[String(c.id)] = c.nom_commission;
      return acc;
    }, {});
  }, [sortedCommissions]);

  const filteredMembres = useMemo(() => {
    const term = normalize(debouncedSearch);

    return [...membres]
      .filter((m) => m && m.id && m.nom && m.prenom)
      .filter((m) => {
        if (!term) return true;

        return normalize(
          [m.prenom, m.nom, m.poste, m.email].filter(Boolean).join(" ")
        ).includes(term);
      })
      .filter((m) => {
        if (!selectedCommissionId) return true;
        if (selectedCommissionId === "none") return !m.commission_id;
        return String(m.commission_id || "") === String(selectedCommissionId);
      })
      .sort((a, b) => {
        const oa = Number(a?.ordre || 0);
        const ob = Number(b?.ordre || 0);
        if (oa !== ob) return oa - ob;

        return normalize(`${a?.prenom} ${a?.nom}`).localeCompare(
          normalize(`${b?.prenom} ${b?.nom}`)
        );
      });
  }, [membres, debouncedSearch, selectedCommissionId]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCommissionId]);

  const totalMembers = filteredMembres.length;
  const totalCommissions = sortedCommissions.length;
  const assignedMembers = useMemo(
    () => membres.filter((m) => !!m.commission_id).length,
    [membres]
  );

  const pageCount = Math.max(1, Math.ceil(filteredMembres.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredMembres.slice(start, start + PAGE_SIZE);
  }, [filteredMembres, safePage]);

  const handleOpenCreateMember = () => {
    setIsEditMode(false);
    setSelectedMemberId(null);
    setIsMemberModalOpen(true);
  };

  const handleEditMember = (id) => {
    setIsEditMode(true);
    setSelectedMemberId(id);
    setIsMemberModalOpen(true);
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm("Confirmer la suppression de ce membre ?")) return;

    try {
      await deleteTeamMember(id);
      setMembres((prev) => prev.filter((m) => m.id !== id));
      addNotification("Membre supprimé avec succès.", "success");
    } catch (error) {
      addNotification("Erreur lors de la suppression du membre.", "error");
      console.error("Erreur lors de la suppression du membre :", error);
    }
  };

  const handleOpenCreateCommission = () => {
    setIsCommissionEditMode(false);
    setSelectedCommissionEditId(null);
    setIsCommissionModalOpen(true);
  };

  const handleEditCommission = (id) => {
    setIsCommissionEditMode(true);
    setSelectedCommissionEditId(id);
    setIsCommissionModalOpen(true);
  };

  const handleDeleteCommission = async (commission) => {
    const membersCount = Array.isArray(commission?.members)
      ? commission.members.length
      : 0;

    if (membersCount > 0) {
      addNotification(
        "Impossible de supprimer une commission qui contient encore des membres. Réaffecte-les d’abord.",
        "error"
      );
      return;
    }

    if (
      !window.confirm(
        `Confirmer la suppression de la commission « ${commission.nom_commission} » ?`
      )
    ) {
      return;
    }

    try {
      await deleteCommission(commission.id);
      setCommissions((prev) => prev.filter((c) => c.id !== commission.id));

      if (String(selectedCommissionId) === String(commission.id)) {
        setSelectedCommissionId("");
      }

      addNotification("Commission supprimée avec succès.", "success");
    } catch (error) {
      addNotification("Erreur lors de la suppression de la commission.", "error");
      console.error("Erreur lors de la suppression de la commission :", error);
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    setSelectedCommissionId("");
    setPage(1);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          totalMembers={totalMembers}
          totalCommissions={totalCommissions}
          assignedMembers={assignedMembers}
        />

        <div className="mt-5">
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCommissionId={selectedCommissionId}
            setSelectedCommissionId={setSelectedCommissionId}
            commissions={sortedCommissions}
            onReset={handleReset}
            onCreateCommission={handleOpenCreateCommission}
            onCreateMember={handleOpenCreateMember}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {totalMembers} membre{totalMembers > 1 ? "s" : ""}
          </span>

          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {totalCommissions} commission{totalCommissions > 1 ? "s" : ""}
          </span>

          {selectedCommissionId && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              {selectedCommissionId === "none"
                ? "Sans commission"
                : `Commission : ${
                    commissionNameById[String(selectedCommissionId)] || selectedCommissionId
                  }`}
            </span>
          )}

          {debouncedSearch && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {searchTerm}
            </span>
          )}
        </div>

        <div className="mt-6">
          <SectionCard
            title="Commissions"
            subtitle="Chaque commission regroupe ses membres et ses articles associés."
          >
            {sortedCommissions.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                Aucune commission disponible.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {sortedCommissions.map((commission) => (
                  <CommissionCard
                    key={commission.id}
                    commission={commission}
                    onEdit={handleEditCommission}
                    onDelete={handleDeleteCommission}
                    onFilterMembers={(id) => {
                      setSelectedCommissionId(String(id));
                      setPage(1);
                    }}
                    isActive={String(selectedCommissionId) === String(commission.id)}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <div className="mt-6">
          <SectionCard
            title="Membres de l’équipe"
            subtitle="Les membres peuvent être rattachés à une commission depuis leur fiche."
          >
            <TeamTableDesktop
              rows={pageItems}
              loading={loading}
              commissionNameById={commissionNameById}
              onEdit={handleEditMember}
              onDelete={handleDeleteMember}
            />

            <TeamCardsMobile
              rows={pageItems}
              loading={loading}
              commissionNameById={commissionNameById}
              onEdit={handleEditMember}
              onDelete={handleDeleteMember}
            />

            <PaginationBar
              page={safePage}
              pageCount={pageCount}
              setPage={setPage}
              loading={loading}
            />
          </SectionCard>
        </div>

        <TeamLigueModal
          isOpen={isMemberModalOpen}
          onClose={() => setIsMemberModalOpen(false)}
          isEditMode={isEditMode}
          teamMemberId={selectedMemberId}
          commissions={sortedCommissions}
          onRefresh={fetchAll}
        />

        <CommissionModal
          isOpen={isCommissionModalOpen}
          onClose={() => setIsCommissionModalOpen(false)}
          isEditMode={isCommissionEditMode}
          commissionId={selectedCommissionEditId}
          onRefresh={fetchAll}
        />
      </div>
    </div>
  );
};

export default TeamLigueAdmin;